import {useCallback, useMemo} from 'react'
import {Box, IconButton, Typography} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../common/store'
import {removeSprite2D, updateSprite2D} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {ScreenSprite} from '../../../common/types/spriteTypes'
import {useAuth} from '../../auth/useAuth'
import {uploadAsset} from '../../../common/api/uploadAsset'
import {cleanupReplacedAsset} from '../../../common/api/cleanupReplacedAsset'
import {loadImageAsDataUrl, measureImageAspect} from '../../../common/utils/spriteUpload'
import {DEFAULT_SPRITE_IMAGE_URL} from '../../../common/constants/defaultAssets'
import SpriteDropZone from './SpriteDropZone'
import OffsetPad from '../sharedWidgets/OffsetPad'
import SliderRow from '../sharedWidgets/SliderRow'
import AnglePicker from '../sharedWidgets/AnglePicker'

interface Sprite2DItemProps {
  sprite: ScreenSprite
}

const Sprite2DItem = ({sprite}: Sprite2DItemProps) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {user} = useAuth()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const handleFieldChange = useCallback(
    (field: keyof ScreenSprite, value: string | number | null) => {
      dispatch(updateSprite2D({id: sprite.id, changes: {[field]: value}}))
    },
    [dispatch, sprite.id],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleApplyFile = useCallback(
    async (file: File) => {
      let imageKey: string | null = null
      let imageUrl: string
      if (user) {
        const result = await uploadAsset(file)
        imageKey = result.key
        imageUrl = result.url
        cleanupReplacedAsset(sprite.imageKey)
      } else {
        imageUrl = await loadImageAsDataUrl(file)
      }
      const imageAspect = await measureImageAspect(imageUrl)
      dispatch(updateSprite2D({id: sprite.id, changes: {imageKey, imageUrl, imageAspect}}))
      dispatch(sealUndo())
    },
    [dispatch, sprite.id, sprite.imageKey, user],
  )

  const handleScaleChange = useCallback((v: number) => handleFieldChange('scale', v), [handleFieldChange])
  const handlePositionX = useCallback((v: number) => handleFieldChange('x', v / 100), [handleFieldChange])
  const handlePositionY = useCallback((v: number) => handleFieldChange('y', v / 100), [handleFieldChange])
  const handleRotation = useCallback((angle: number) => handleFieldChange('rotation', angle), [handleFieldChange])

  const handleRemove = useCallback(() => dispatch(removeSprite2D(sprite.id)), [dispatch, sprite.id])

  return (
    <Box sx={sx.root}>
      <Box sx={sx.headerRow}>
        <Box sx={sx.dropZoneSlot}>
          <SpriteDropZone
            imageUrl={sprite.imageUrl}
            isDefaultImage={sprite.imageUrl === DEFAULT_SPRITE_IMAGE_URL}
            onApplyFile={handleApplyFile}
          />
        </Box>
        <IconButton onClick={handleRemove} size="small" sx={sx.deleteBtn} title={t('sprite2d.remove')}>
          <DeleteIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>

      <SliderRow
        label={t('sprite2d.scale')}
        value={sprite.scale}
        min={0.1}
        max={5}
        step={0.05}
        unit=""
        onChange={handleScaleChange}
        onChangeCommitted={handleSealUndo}
      />

      <Box sx={sx.positionSection}>
        <Typography sx={sx.label}>{t('sprite2d.position')}</Typography>
        <OffsetPad
          valueX={Math.round(sprite.x * 100)}
          valueY={Math.round(sprite.y * 100)}
          min={0}
          max={100}
          onChangeX={handlePositionX}
          onChangeY={handlePositionY}
          onDragEnd={handleSealUndo}
        />
      </Box>

      <Box sx={sx.rotationSection}>
        <Typography sx={sx.label}>{t('sprite2d.rotation')}</Typography>
        <Box sx={sx.rotationRow}>
          <AnglePicker value={sprite.rotation} onChange={handleRotation} onDragEnd={handleSealUndo} />
          <Typography sx={sx.rotationValue}>{sprite.rotation}°</Typography>
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    padding: '8px',
    marginBottom: '8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${tokens.accordion.border}`,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    marginBottom: '4px',
  },
  dropZoneSlot: {
    flex: 1,
    minWidth: 0,
  },
  deleteBtn: {
    color: tokens.text.muted,
    marginTop: '2px',
    '&:hover': {color: '#ef5350'},
  },
  label: {
    fontSize: 11,
    color: tokens.text.secondary,
    whiteSpace: 'nowrap' as const,
  },
  positionSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
    marginTop: '8px',
  },
  rotationSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginTop: '8px',
  },
  rotationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  rotationValue: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
  },
})

export default Sprite2DItem
